import { useTranslation } from 'react-i18next';
import type { tConnectionFailedAdvisorCtaKind } from 'src/components/WhatsAppConnect/ConnectionFailedError/ConnectionFailedError.types';
import { getEmbeddedSignupAttemptId } from 'src/lib/facebook/embeddedSignupTelemetry';
import {
  WIZARD_BODY_CLASS_NAME,
  WIZARD_CAPTION_CLASS_NAME,
  WIZARD_SCREEN_SUBTITLE_CLASS_NAME,
  WIZARD_SCREEN_TITLE_CLASS_NAME,
  WIZARD_TRANSLATION_NAMESPACE,
} from './WhatsAppConnectionWizard.constants';
import { WhatsAppConnectionWizardAdvisorRow } from './WhatsAppConnectionWizardExternalLink.component';

export type tWhatsAppConnectionWizardExchangeErrorProps = {
  /** Whether an exchange error with no verdict came right before this one. */
  isRepeat: boolean;
  onAdvisorCtaClick: (kind: tConnectionFailedAdvisorCtaKind) => void;
};

/**
 * The main pane when exchanging Meta's code failed with no verdict, such as a
 * 500 or the wait limit. Nothing says whose failure it was, so the copy does
 * not blame Meta. The first time its body ends with the advisor row; from the
 * second time on the footer leads with the advisor instead.
 *
 * @param {tWhatsAppConnectionWizardExchangeErrorProps} props - Whether it is a
 * repeat, and the advisor click report.
 * @returns {JSX.Element} The exchange error screen.
 */
export default function WhatsAppConnectionWizardExchangeError(
  props: tWhatsAppConnectionWizardExchangeErrorProps,
) {
  const { isRepeat, onAdvisorCtaClick } = props;
  const { t } = useTranslation(WIZARD_TRANSLATION_NAMESPACE);
  // Kept in memory only, so a failure restored after a reload has none, and
  // no code is shown rather than one made up.
  const attemptId = getEmbeddedSignupAttemptId();

  return (
    <div className="flex flex-col gap-[var(--ls-spacing-6)]">
      <div className="flex flex-col gap-[var(--ls-spacing-2)]">
        <h2 className={WIZARD_SCREEN_TITLE_CLASS_NAME}>
          {t('wizard.exchangeError.title')}
        </h2>
        <p className={WIZARD_SCREEN_SUBTITLE_CLASS_NAME}>
          {t('wizard.exchangeError.subtitle')}
        </p>
      </div>
      <div className="flex flex-col items-start gap-[var(--ls-spacing-2)]">
        <p className={WIZARD_BODY_CLASS_NAME}>
          {t(
            isRepeat
              ? 'wizard.exchangeError.repeatBody'
              : 'wizard.exchangeError.supportLine',
          )}
        </p>
        {attemptId && (
          <p className={WIZARD_CAPTION_CLASS_NAME}>
            {t('wizard.closedBeforeFinishing.attemptCodeLine', { attemptId })}
          </p>
        )}
      </div>
      {/* A repeat puts the advisor in the footer's primary. */}
      {!isRepeat && (
        <WhatsAppConnectionWizardAdvisorRow
          onAdvisorCtaClick={onAdvisorCtaClick}
        />
      )}
    </div>
  );
}
